/**
 * StatusTooltip.jsx
 * 
 * Reusable tooltip component for device status badges.
 * Shows clear explanation of Online / Degraded / Offline / Unknown.
 * 
 * Place in: /src/components/StatusTooltip.jsx
 * 
 * Usage:
 *   <StatusTooltip status="DEGRADED">
 *     <span className="status-indicator degraded">Degraded</span>
 *   </StatusTooltip>
 */

import { useState, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';

const STATUS_INFO = {
  ONLINE: {
    icon: CheckCircle,
    color: '#22c55e',
    title: 'Online',
    description: 'Device is reachable and all monitored services are responding normally.',
  },
  DEGRADED: {
    icon: AlertTriangle,
    color: '#ffaa00',
    title: 'Degraded',
    description: 'Device is reachable but something is wrong. This could mean high latency, partial response, a service down on the device, or responding on some ports but not others.',
  },
  OFFLINE: {
    icon: XCircle,
    color: '#ef4444',
    title: 'Offline',
    description: 'Device is completely unreachable. No response to ping or any network probe. Check power, cabling, and network connectivity.',
  },
  MAINTENANCE: {
    icon: HelpCircle,
    color: '#888',
    title: 'Maintenance',
    description: 'Device has been manually placed in maintenance mode. Monitoring checks and alerts are suppressed.',
  },
  PAUSED: {
    icon: HelpCircle,
    color: '#666',
    title: 'Paused',
    description: 'Monitoring for this device has been paused. No health checks are being performed.',
  },
  UNKNOWN: {
    icon: HelpCircle,
    color: '#64748b',
    title: 'Unknown',
    description: 'Device status has not been determined yet. It may be newly added or the first check has not completed.',
  },
  STALE: {
    icon: HelpCircle,
    color: '#64748b',
    title: 'Stale',
    description: 'Not monitored and not seen recently. This does not mean the device is down, only that it has not been rescanned. It updates automatically on the next scan.',
  },
  NEVER_SYNCED: {
    icon: HelpCircle,
    color: '#64748b',
    title: 'Never Synced',
    description: 'This device has not been checked yet. The monitoring agent will check it on the next poll cycle.',
  },
};

const StatusTooltip = ({ status, children, position = 'bottom' }) => {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef(null);

  const info = STATUS_INFO[status] || STATUS_INFO.UNKNOWN;
  const Icon = info.icon;

  const showTip = () => {
    timeoutRef.current = setTimeout(() => setVisible(true), 300);
  };

  const hideTip = () => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  return (
    <span
      className="status-tooltip-wrapper"
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      {children}
      {visible && (
        <div className={`status-tooltip status-tooltip-${position}`}>
          <div className="status-tooltip-header">
            <Icon size={14} style={{ color: info.color, flexShrink: 0 }} />
            <span className="status-tooltip-title" style={{ color: info.color }}>{info.title}</span>
          </div>
          <p className="status-tooltip-desc">{info.description}</p>
        </div>
      )}
    </span>
  );
};

export default StatusTooltip;
